import React, { useState } from 'react';
import type { AdminTransferDetail } from '@platform/shared-types';
import { businessTransferApi } from '../../../services/businessTransferApi';
import { toastService } from '../../../services/toastService';
import { when } from '../format';

type P = { detail: AdminTransferDetail; canAct: boolean; onChange: (d: AdminTransferDetail) => void };
const LABEL: Record<string, string> = {
  products: 'Products', variants: 'Variants', categories: 'Categories', brands: 'Brands', suppliers: 'Suppliers', taxes: 'Tax rules',
  modifierGroups: 'Modifier groups', promotions: 'Promotions', barcodes: 'Barcodes', images: 'Images', prices: 'Store prices',
  stockUnits: 'Stock on hand', customers: 'Customers', staff: 'Staff', registers: 'Registers', cardReaders: 'Card readers',
  onlineGateway: 'Online payments', kiosks: 'Kiosks', adScreens: 'Ad screens', leasedDevices: 'Leased devices', deviceRecords: 'Device records',
};

export default function DryRunPanel({ detail, canAct, onChange }: P) {
  const d = detail.transfer.dryRun;
  const [busy, setBusy] = useState(false);
  const rerun = async () => {
    setBusy(true);
    try {
      await businessTransferApi.dryRun(detail.transfer.transferId);
      toastService.success('Dry run queued — refresh in a minute');
      onChange(await businessTransferApi.get(detail.transfer.transferId));
    } catch (e: any) { toastService.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-stone-500">{d ? `Ran ${when(d.ranAt)} · nothing written` : 'Not run yet'}</span>
        {canAct && !detail.transfer.run && (
          <button type="button" disabled={busy} onClick={rerun} className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 text-xs font-semibold">Re-run dry run</button>
        )}
      </div>
      {d && d.blockers.map((b) => (
        <div key={b.code + b.message} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800"><b>Blocking · {b.owner}:</b> {b.message}</div>
      ))}
      {d && d.warnings.map((w) => (
        <div key={w.code + w.message} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">{w.message}</div>
      ))}
      {d && (
        <table className="w-full"><tbody>
          {Object.entries(d.counts).map(([k, v]) => (
            <tr key={k} className="border-t border-stone-100"><td className="py-1.5">{LABEL[k] || k}</td><td className="py-1.5 text-right font-mono">{v.toLocaleString()}</td></tr>
          ))}
          <tr className="border-t border-stone-100"><td className="py-1.5">Loyalty points copied (also kept by the seller)</td><td className="py-1.5 text-right font-mono">{d.loyaltyPoints.toLocaleString()}</td></tr>
        </tbody></table>
      )}
      {d && d.skipped.length > 0 && (
        <details><summary className="cursor-pointer text-rose-800">{d.skipped.length} not copied</summary>
          <ul className="mt-1 list-disc pl-5 text-stone-600">{d.skipped.map((s) => <li key={s.id}>{s.label} — {s.reason}</li>)}</ul>
        </details>
      )}
    </div>
  );
}
